import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';

/**
 * Onboarding Modal Component
 * Shows first-time guide with steps, skip, and "don't show again" features
 */
const OnboardingModal = ({ show, onClose, steps, storageKey = 'onboarding_completed', language = 'en' }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const tr = language === 'tr';

  // Auto-show logic: check both sessionStorage (this session) and localStorage (permanent)
  useEffect(() => {
    if (show) {
      const sessionCompleted = sessionStorage.getItem(storageKey);
      const permanentlyHidden = localStorage.getItem(storageKey);
      
      if (sessionCompleted === 'true' || permanentlyHidden === 'true') {
        onClose();
      }
    }
  }, [show, storageKey]); // onClose intentionally omitted to prevent infinite loop

  if (!show || !steps || steps.length === 0) return null;

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;

  const handleNext = () => {
    if (isLast) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirst) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    // Mark as completed for this session only
    sessionStorage.setItem(storageKey, 'true');
    
    if (dontShowAgain) {
      localStorage.setItem(storageKey, 'true');
    }
    onClose();
  };

  const handleComplete = () => {
    // Mark as completed for this session only
    sessionStorage.setItem(storageKey, 'true');
    
    if (dontShowAgain) {
      localStorage.setItem(storageKey, 'true');
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden relative">
        {/* Close Button */}
        <button
          onClick={handleSkip}
          className="absolute top-4 right-4 z-10 h-8 w-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
          <X className="h-5 w-5" />
        </button>

        {/* Header with Progress */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold text-white">
              {step.title}
            </h3>
            <div className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full">
              <span className="text-white text-sm font-semibold">
                {currentStep + 1} / {steps.length}
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Icon/Image */}
          {step.icon && (
            <div className="flex justify-center mb-4">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center">
                {typeof step.icon === 'string' ? (
                  <span className="text-3xl">{step.icon}</span>
                ) : (
                  <step.icon className="h-8 w-8 text-emerald-600" />
                )}
              </div>
            </div>
          )}

          {/* Description */}
          <div className="text-center mb-6">
            <p className="text-gray-700 text-base leading-relaxed">
              {step.description}
            </p>
          </div>

          {/* Checklist */}
          {step.checklist && step.checklist.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <ul className="space-y-2">
                {step.checklist.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Don't Show Again Checkbox */}
          {isLast && (
            <div className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                id="dont-show-again"
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              <label htmlFor="dont-show-again" className="text-sm text-gray-600 cursor-pointer">
                {tr ? 'Bir daha gösterme' : "Don't show again"}
              </label>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleSkip}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors">
            {tr ? 'Atla' : 'Skip'}
          </button>

          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={handlePrevious}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1">
                <ChevronLeft className="h-4 w-4" />
                {tr ? 'Geri' : 'Previous'}
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-5 py-1.5 text-sm font-bold text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-1">
              {isLast ? (
                <>
                  {tr ? 'Tamamla' : 'Complete'}
                  <CheckCircle2 className="h-4 w-4" />
                </>
              ) : (
                <>
                  {tr ? 'İleri' : 'Next'}
                  <ChevronRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
